// @section 1 v5 Top bar — pipeline hero + supporting metrics + clock + user.
// Wired to /api/hq/founder-os/top-bar. Sparkline is still a CSS rendering of
// a fixed shape (the visual "rising bars" pattern); Phase 5 swaps in a true
// 7-day series once we have the snapshot table.

import { useEffect, useState } from 'react';
import { FoTopBar, foGetTopBar } from '../api';
import { useMeUser } from '../lib/useMe';

const SPARK_HEIGHTS = [30, 38, 45, 55, 62, 75, 88];

function useClock() {
  const [t, setT] = useState(() => new Date());
  useEffect(() => {
    const i = window.setInterval(() => setT(new Date()), 30_000);
    return () => window.clearInterval(i);
  }, []);
  return t;
}

function initials(name?: string): string {
  if (!name) return '·';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function shortName(name?: string): string {
  if (!name) return 'Atlas';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function fmtUsdK(cents: number | null | undefined): string {
  if (cents == null) return '—';
  const usd = cents / 100;
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(1)}M`;
  if (usd >= 1_000)     return `$${Math.round(usd / 1_000)}K`;
  return `$${usd.toFixed(0)}`;
}

export function TopBar({ onMenu }: { onMenu?: () => void } = {}) {
  const me = useMeUser();
  const now = useClock();
  const [data, setData] = useState<FoTopBar | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = () => foGetTopBar().then(d => !cancelled && setData(d)).catch(() => { /* keep last */ });
    void fetchOnce();
    const i = window.setInterval(fetchOnce, 5 * 60_000); // refresh every 5 min
    return () => { cancelled = true; window.clearInterval(i); };
  }, []);

  const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const pipeline   = fmtUsdK(data?.pipeline_total_cents);
  const delta      = data?.week_delta_cents ?? 0;
  const deltaStr   = delta === 0 ? '—' : `${delta > 0 ? '↑' : '↓'} ${fmtUsdK(Math.abs(delta))} · 7d`;
  const mrr        = fmtUsdK(data?.mrr_cents);
  const cash       = fmtUsdK(data?.cash_cents);
  const cashRunway = data?.cash_months != null ? ` · ${Math.round(Number(data.cash_months))}mo` : '';
  const bookings   = data?.bookings_7d ?? 0;
  const captures   = data?.captures_pending ?? 0;

  return (
    <header className="v5-top-bar">
      {/* Mobile-only hamburger (hidden >820px via v5.css). Toggles the off-canvas sidebar. */}
      <button className="v5-menu-btn" onClick={onMenu} aria-label="Open menu">☰</button>
      <div className="brand-block">
        <div className="brand-mark">A</div>
        <div className="brand-text">
          <div className="name">Atlas</div>
          <div className="sub">By OptiqOS</div>
        </div>
      </div>

      <div className="pipe-hero">
        <div>
          <div className="lbl">Pipeline</div>
          <div className="val">{pipeline}</div>
          <div className="delta">{deltaStr}</div>
        </div>
        <div className="spark">
          {SPARK_HEIGHTS.map((h, i) => (<span key={i} style={{ height: `${h}%` }} />))}
        </div>
      </div>

      <div className="support-metrics">
        <div className="support"><div className="lbl">MRR</div><div className="val">{mrr}</div></div>
        <div className="support"><div className="lbl">Cash</div><div className={`val${data?.cash_months != null && data.cash_months < 9 ? ' warn' : ''}`}>{cash}{cashRunway}</div></div>
        <div className="support"><div className="lbl">Bookings · 7d</div><div className="val">{bookings}</div></div>
        <div className="support"><div className="lbl">Captures</div><div className="val">{captures}</div></div>
      </div>

      <div className="top-right">
        <span className="live-pill">Live</span>
        <span className="clock">{time} · {date}</span>
        <div className="user-chip" title={me?.email}>
          <div className="ava">{initials(me?.displayName)}</div>
          <span>{shortName(me?.displayName)}</span>
        </div>
      </div>
    </header>
  );
}
